/**
 * Lightweight namespaced logger with level filtering.
 *
 * Usage:
 *   import { createLogger } from '../utils/logger.js'
 *   const log = createLogger('choropleth')
 *   log.debug('rendering', { features: 42 })
 *   log.info('layer ready')
 *   log.warn('catalog fallback used')
 *   log.error('pipeline failed', err)
 *
 * Level control (in browser devtools):
 *   localStorage.setItem('ev:log', 'debug')   // see everything
 *   localStorage.setItem('ev:log', 'warn')    // warn + error only
 *   localStorage.removeItem('ev:log')         // back to default
 *
 * Levels (ascending severity):
 *   debug < info < warn < error
 *
 * Default level:
 *   dev: 'info'   (debug hidden unless explicitly enabled)
 *   prod: 'warn'  (info/debug are tree-shaken by Vite esbuild.pure)
 *
 * Production behavior:
 *   In production builds, log.debug() and log.info() calls are tree-shaken
 *   via Vite's esbuild.pure config (which already drops console.log/debug).
 *   log.warn() and log.error() still print for observability.
 */

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 }

function getActiveLevel() {
  try {
    const stored = typeof localStorage !== 'undefined' && localStorage.getItem('ev:log')
    if (stored && stored in LEVELS) return LEVELS[stored]
  } catch {
    // localStorage access can throw in sandboxed contexts; fall through to default
  }
  // import.meta.env.DEV is true in vite dev, undefined/false in prod
  return typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV
    ? LEVELS.info
    : LEVELS.warn
}

export function createLogger(namespace) {
  const tag = `[${namespace}]`
  return {
    debug(...args) {
      // eslint-disable-next-line no-console
      if (getActiveLevel() <= LEVELS.debug) console.log(tag, ...args)
    },
    info(...args) {
      // eslint-disable-next-line no-console
      if (getActiveLevel() <= LEVELS.info) console.log(tag, ...args)
    },
    warn(...args) {
      // eslint-disable-next-line no-console
      if (getActiveLevel() <= LEVELS.warn) console.warn(tag, ...args)
    },
    error(...args) {
      // eslint-disable-next-line no-console
      if (getActiveLevel() <= LEVELS.error) console.error(tag, ...args)
    },
  }
}

/**
 * Default logger for ad-hoc use without a namespace.
 * Prefer createLogger() with a namespace in module-scoped code.
 */
export const log = createLogger('ev')
